import type { SeededRandom } from '../SeededRandom.ts';
import { z } from 'zod';

export const expressionOperatorDescriptorSchema = z.object({
  name: z.string().regex(/^[a-z_]\w*$/i),
  title: z.string().min(1),
  description: z.string().default(''),
  category: z.enum(['math', 'collection', 'random', 'other']),
  minArgs: z.number().int().nonnegative(),
  maxArgs: z.number().int().nonnegative(),
  deterministic: z.boolean(),
  batchScalarOutput: z.boolean().default(false),
  callForms: z.array(z.object({
    description: z.string().min(1),
    arguments: z.array(z.object({
      name: z.string().min(1),
      description: z.string().min(1),
      literalValues: z.array(
        z.union([z.string(), z.number(), z.boolean(), z.null()]),
      ).min(1).optional(),
    }).strict()).max(8),
  }).strict()).min(1).optional(),
}).strict().refine(
  descriptor => descriptor.maxArgs >= descriptor.minArgs,
  { message: '运算符最大参数数量不能小于最小参数数量' },
);

// Registration accepts the schema input so existing operators do not need to
// repeat default-valued metadata.  The registry still parses every descriptor
// before storing/serializing it, therefore published capabilities always carry
// an explicit boolean value.
export type ExpressionOperatorDescriptor = z.input<
  typeof expressionOperatorDescriptorSchema
>;

export interface ExpressionOperatorContext {
  random: SeededRandom;
}

export interface ExpressionOperatorDefinition {
  descriptor: ExpressionOperatorDescriptor;
  execute: (
    args: unknown[],
    context: ExpressionOperatorContext,
  ) => unknown;
}

export class ExpressionOperatorRegistry {
  private readonly definitions = new Map<
    string,
    ExpressionOperatorDefinition
  >();

  register(rawDefinition: ExpressionOperatorDefinition): this {
    const descriptor = expressionOperatorDescriptorSchema.parse(
      rawDefinition.descriptor,
    );
    if (this.definitions.has(descriptor.name))
      throw new Error(`表达式运算符已经注册：${descriptor.name}`);
    this.definitions.set(descriptor.name, {
      ...rawDefinition,
      descriptor,
    });
    return this;
  }

  has(name: string): boolean {
    return this.definitions.has(name);
  }

  resolve(name: string): ExpressionOperatorDefinition {
    const definition = this.definitions.get(name);
    if (!definition)
      throw new Error(`表达式运算符尚未注册：${name}`);
    return definition;
  }

  listDescriptors(): ExpressionOperatorDescriptor[] {
    return [...this.definitions.values()].map(definition =>
      expressionOperatorDescriptorSchema.parse(
        structuredClone(definition.descriptor),
      ),
    );
  }

  createScope(context: ExpressionOperatorContext): Record<
    string,
    (...args: unknown[]) => unknown
  > {
    return Object.fromEntries(
      [...this.definitions.entries()].map(([name, definition]) => [
        name,
        (...args: unknown[]) => {
          const { minArgs, maxArgs } = definition.descriptor;
          if (args.length < minArgs || args.length > maxArgs) {
            throw new Error(
              `${name} 需要 ${minArgs === maxArgs ? minArgs : `${minArgs}-${maxArgs}`} 个参数`,
            );
          }
          return definition.execute(args, context);
        },
      ]),
    );
  }
}

function finite(name: string, value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value))
    throw new Error(`${name} 的参数必须是有限数值`);
  return value;
}

function numeric(
  name: string,
  execute: (...values: number[]) => number,
  minArgs: number,
  maxArgs = minArgs,
): ExpressionOperatorDefinition {
  return {
    descriptor: {
      name,
      title: name,
      description: '',
      category: 'math',
      minArgs,
      maxArgs,
      deterministic: true,
    },
    execute: args => execute(...args.map(value => finite(name, value))),
  };
}

function positiveSampleCount(value: unknown): number {
  const count = finite('sampleMean', value);
  if (!Number.isInteger(count) || count < 1 || count > 10000)
    throw new Error('sampleMean 的样本量必须是 1 到 10000 的整数');
  return count;
}

function boundedInteger(name: string, value: unknown, maximum: number): number {
  const result = finite(name, value);
  if (!Number.isInteger(result) || result < 1 || result > maximum)
    throw new Error(`${name} 的参数必须是 1 到 ${maximum} 的整数`);
  return result;
}

function boundedNonNegativeInteger(
  name: string,
  value: unknown,
  maximum: number,
): number {
  const result = finite(name, value);
  if (!Number.isInteger(result) || result < 0 || result > maximum)
    throw new Error(`${name} 的参数必须是 0 到 ${maximum} 的整数`);
  return result;
}

function standardNormal(random: SeededRandom): number {
  const first = Math.max(Number.EPSILON, random.next());
  const second = random.next();
  return Math.sqrt(-2 * Math.log(first)) * Math.cos(2 * Math.PI * second);
}

function standardNormalCdf(value: number): number {
  // Abramowitz-Stegun 7.1.26.  The approximation is deterministic, bounded,
  // and accurate enough for pricing/teaching displays (absolute error below
  // 1.5e-7) without exposing arbitrary JavaScript to generated models.
  const sign = value < 0 ? -1 : 1;
  const x = Math.abs(value) / Math.sqrt(2);
  const t = 1 / (1 + 0.3275911 * x);
  const erf = sign * (1 - (
    (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t
      - 0.284496736) * t + 0.254829592) * t
  ) * Math.exp(-x * x));
  return Math.min(1, Math.max(0, 0.5 * (1 + erf)));
}

function poissonSample(random: SeededRandom, rate: number): number {
  if (rate === 0)
    return 0;
  if (rate >= 30)
    return Math.max(0, Math.round(rate + Math.sqrt(rate) * standardNormal(random)));
  const limit = Math.exp(-rate);
  let product = 1;
  let count = 0;
  do {
    count += 1;
    product *= random.next();
  } while (product > limit);
  return count - 1;
}

function sampleDistribution(
  name: string,
  args: number[],
  random: SeededRandom,
): number {
  if (name === 'exponential') {
    const rate = args[0];
    if (rate === undefined || rate <= 0)
      throw new Error('sampleAggregate exponential 需要正速率');
    return random.exponential(rate);
  }
  if (name === 'uniform') {
    const [minimum, maximum] = args;
    if (minimum === undefined || maximum === undefined || maximum < minimum)
      throw new Error('sampleAggregate uniform 需要合法上下界');
    return minimum + random.next() * (maximum - minimum);
  }
  if (name === 'bernoulli') {
    const probability = args[0];
    if (probability === undefined || probability < 0 || probability > 1)
      throw new Error('sampleAggregate bernoulli 需要 [0,1] 概率');
    return random.next() < probability ? 1 : 0;
  }
  if (name === 'normal') {
    const [mean, deviation] = args;
    if (mean === undefined || deviation === undefined || deviation < 0)
      throw new Error('sampleAggregate normal 需要均值和非负标准差');
    return mean + deviation * standardNormal(random);
  }
  if (name === 'poisson') {
    const rate = args[0];
    if (rate === undefined || rate < 0)
      throw new Error('sampleAggregate poisson 需要非负参数');
    return poissonSample(random, rate);
  }
  throw new Error(`sampleAggregate 不支持分布：${name}`);
}

export function createBuiltinExpressionOperatorRegistry():
ExpressionOperatorRegistry {
  const registry = new ExpressionOperatorRegistry();
  for (const definition of [
    numeric('abs', Math.abs, 1),
    numeric('ceil', Math.ceil, 1),
    numeric('cos', Math.cos, 1),
    numeric('exp', Math.exp, 1),
    numeric('floor', Math.floor, 1),
    numeric('log', Math.log, 1),
    numeric('max', Math.max, 1, 1000),
    numeric('min', Math.min, 1, 1000),
    numeric('normalCdf', standardNormalCdf, 1),
    numeric('pow', Math.pow, 2),
    numeric('round', Math.round, 1),
    numeric('sin', Math.sin, 1),
    numeric('sqrt', Math.sqrt, 1),
    numeric('tan', Math.tan, 1),
  ]) {
    registry.register(definition);
  }

  return registry
    .register({
      descriptor: {
        name: 'clamp',
        title: '范围截断',
        description: '将数值限制在闭区间内。',
        category: 'math',
        minArgs: 3,
        maxArgs: 3,
        deterministic: true,
      },
      execute: ([rawValue, rawMin, rawMax]) => {
        const value = finite('clamp', rawValue);
        const min = finite('clamp', rawMin);
        const max = finite('clamp', rawMax);
        if (max < min)
          throw new Error('clamp 上限不能小于下限');
        return Math.min(Math.max(value, min), max);
      },
    })
    .register({
      descriptor: {
        name: 'count',
        title: '集合数量',
        description: '返回数组长度或对象键数量。',
        category: 'collection',
        minArgs: 1,
        maxArgs: 1,
        deterministic: true,
      },
      execute: ([value]) =>
        Array.isArray(value)
          ? value.length
          : value && typeof value === 'object'
            ? Object.keys(value).length
            : 0,
    })
    .register({
      descriptor: {
        name: 'concat',
        title: '字符串连接',
        description: '将多个值按顺序连接为一个字符串。',
        category: 'other',
        minArgs: 1,
        maxArgs: 1000,
        deterministic: true,
      },
      execute: args => args.map(value => String(value)).join(''),
    })
    .register({
      descriptor: {
        name: 'first',
        title: '首个元素',
        description: '返回数组首个元素，空数组返回 null。',
        category: 'collection',
        minArgs: 1,
        maxArgs: 1,
        deterministic: true,
      },
      execute: ([value]) => {
        if (!Array.isArray(value))
          throw new Error('first 的参数必须是数组');
        return value[0] ?? null;
      },
    })
    .register({
      descriptor: {
        name: 'last',
        title: '末尾元素',
        description: '返回数组末尾元素，空数组返回 null。',
        category: 'collection',
        minArgs: 1,
        maxArgs: 1,
        deterministic: true,
      },
      execute: ([value]) => {
        if (!Array.isArray(value))
          throw new Error('last 的参数必须是数组');
        return value.at(-1) ?? null;
      },
    })
    .register({
      descriptor: {
        name: 'random',
        title: '均匀随机数',
        description: '返回 [0, 1) 的可复现伪随机数。',
        category: 'random',
        minArgs: 0,
        maxArgs: 0,
        deterministic: false,
      },
      execute: (_args, context) => context.random.next(),
    })
    .register({
      descriptor: {
        name: 'uniform',
        title: '均匀分布',
        description: '从指定闭区间采样。',
        category: 'random',
        minArgs: 2,
        maxArgs: 2,
        deterministic: false,
      },
      execute: ([rawMin, rawMax], context) => {
        const min = finite('uniform', rawMin);
        const max = finite('uniform', rawMax);
        if (max < min)
          throw new Error('均匀分布上限不能小于下限');
        return min + context.random.next() * (max - min);
      },
    })
    .register({
      descriptor: {
        name: 'exponential',
        title: '指数分布',
        description: '按正速率参数采样指数分布。',
        category: 'random',
        minArgs: 1,
        maxArgs: 1,
        deterministic: false,
      },
      execute: ([rawRate], context) =>
        context.random.exponential(finite('exponential', rawRate)),
    })
    .register({
      descriptor: {
        name: 'sampleMean',
        title: '重复抽样均值',
        description: '从指定分布独立抽样 1–10000 次并返回均值。参数为分布名、样本量及分布参数。',
        category: 'random',
        minArgs: 3,
        maxArgs: 4,
        deterministic: false,
        batchScalarOutput: true,
        callForms: [
          {
            description: '从指数分布、伯努利分布或泊松分布重复抽样并计算均值。',
            arguments: [
              {
                name: 'distribution',
                description: '分布名必须直接选择 exponential、bernoulli 或 poisson。',
                literalValues: ['exponential', 'bernoulli', 'poisson'],
              },
              { name: 'count', description: '1 到 10000 的样本量数值表达式。' },
              { name: 'parameter', description: '该分布唯一的数值参数表达式。' },
            ],
          },
          {
            description: '从均匀分布重复抽样并计算均值。',
            arguments: [
              {
                name: 'distribution',
                description: '分布名必须直接填写 uniform。',
                literalValues: ['uniform'],
              },
              { name: 'count', description: '1 到 10000 的样本量数值表达式。' },
              { name: 'minimum', description: '均匀分布下界数值表达式。' },
              { name: 'maximum', description: '不小于下界的上界数值表达式。' },
            ],
          },
          {
            description: '从正态分布重复抽样并计算均值。',
            arguments: [
              {
                name: 'distribution',
                description: '分布名必须直接填写 normal。',
                literalValues: ['normal'],
              },
              { name: 'count', description: '1 到 10000 的样本量数值表达式。' },
              { name: 'mean', description: '正态分布均值表达式。' },
              { name: 'deviation', description: '非负标准差表达式。' },
            ],
          },
        ],
      },
      execute: ([rawDistribution, rawCount, rawFirst, rawSecond], context) => {
        if (typeof rawDistribution !== 'string')
          throw new Error('sampleMean 的第一个参数必须是分布名');
        const count = positiveSampleCount(rawCount);
        const first = finite('sampleMean', rawFirst);
        const second = rawSecond === undefined
          ? undefined
          : finite('sampleMean', rawSecond);
        let total = 0;
        for (let index = 0; index < count; index += 1) {
          if (rawDistribution === 'exponential') {
            total += context.random.exponential(first);
          }
          else if (rawDistribution === 'uniform') {
            if (second === undefined || second < first)
              throw new Error('sampleMean uniform 需要合法的上下界');
            total += first + context.random.next() * (second - first);
          }
          else if (rawDistribution === 'bernoulli') {
            if (first < 0 || first > 1)
              throw new Error('sampleMean bernoulli 概率必须在 0 到 1 之间');
            total += context.random.next() < first ? 1 : 0;
          }
          else if (rawDistribution === 'normal') {
            const deviation = second ?? 1;
            if (deviation < 0)
              throw new Error('sampleMean normal 标准差不能小于 0');
            total += first + deviation * standardNormal(context.random);
          }
          else if (rawDistribution === 'poisson') {
            if (first < 0)
              throw new Error('sampleMean poisson 参数不能小于 0');
            total += poissonSample(context.random, first);
          }
          else {
            throw new Error(`sampleMean 不支持分布：${rawDistribution}`);
          }
        }
        return total / count;
      },
    })
    .register({
      descriptor: {
        name: 'sampleAggregate',
        title: '有界重复抽样聚合',
        description: '从指定 production 分布独立抽样 1–10000 次，并执行受限 reducer。',
        category: 'random',
        minArgs: 4,
        maxArgs: 5,
        deterministic: false,
        batchScalarOutput: true,
        callForms: [{
          description: '参数依次为分布名、样本量、reducer 和分布参数。',
          arguments: [
            { name: 'distribution', description: 'exponential/uniform/bernoulli/normal/poisson。', literalValues: ['exponential', 'uniform', 'bernoulli', 'normal', 'poisson'] },
            { name: 'count', description: '1 到 10000。' },
            { name: 'reducer', description: '受限聚合。', literalValues: ['sum', 'mean', 'count', 'min', 'max', 'any', 'all', 'uniqueCount', 'hasDuplicate', 'proportion'] },
            { name: 'parameter', description: '第一个分布参数。' },
            { name: 'parameter2', description: '可选第二个分布参数。' },
          ],
        }],
      },
      execute: ([rawDistribution, rawCount, rawReducer, ...rawArgs], context) => {
        if (typeof rawDistribution !== 'string' || typeof rawReducer !== 'string')
          throw new Error('sampleAggregate 的分布名和 reducer 必须是字符串');
        const count = positiveSampleCount(rawCount);
        const args = rawArgs.map(value => finite('sampleAggregate', value));
        const values = Array.from({ length: count }, () =>
          sampleDistribution(rawDistribution, args, context.random));
        if (rawReducer === 'sum')
          return values.reduce((total, value) => total + value, 0);
        if (rawReducer === 'mean')
          return values.reduce((total, value) => total + value, 0) / count;
        if (rawReducer === 'count')
          return count;
        if (rawReducer === 'min')
          return Math.min(...values);
        if (rawReducer === 'max')
          return Math.max(...values);
        if (rawReducer === 'any')
          return values.some(Boolean);
        if (rawReducer === 'all')
          return values.every(Boolean);
        if (rawReducer === 'proportion')
          return values.filter(Boolean).length / count;
        if (rawReducer === 'uniqueCount' || rawReducer === 'hasDuplicate') {
          const distinct = new Set(values).size;
          return rawReducer === 'uniqueCount' ? distinct : distinct < count;
        }
        throw new Error(`sampleAggregate 不支持 reducer：${rawReducer}`);
      },
    })
    .register({
      descriptor: {
        name: 'sampleCollisionGroupCount',
        title: '多组离散抽样碰撞计数',
        description: '独立完成多组有放回等概率离散抽样，返回至少出现一次重复类别的组数。一次调用就是一批完整独立实验。',
        category: 'random',
        minArgs: 3,
        maxArgs: 3,
        deterministic: false,
        batchScalarOutput: true,
        callForms: [{
          description: '完成 groupCount 组独立的占用/碰撞实验。',
          arguments: [
            { name: 'groupCount', description: '1 到 10000 的独立实验组数。' },
            { name: 'drawsPerGroup', description: '每组 1 到 10000 次抽样。' },
            { name: 'categoryCount', description: '1 到 1000000 的等概率类别数。' },
          ],
        }],
      },
      execute: ([rawGroupCount, rawDrawsPerGroup, rawCategoryCount], context) => {
        const groupCount = boundedInteger(
          'sampleCollisionGroupCount',
          rawGroupCount,
          10000,
        );
        const drawsPerGroup = boundedInteger(
          'sampleCollisionGroupCount',
          rawDrawsPerGroup,
          10000,
        );
        const categoryCount = boundedInteger(
          'sampleCollisionGroupCount',
          rawCategoryCount,
          1_000_000,
        );
        let collisionProbability = 1;
        if (drawsPerGroup <= categoryCount) {
          let distinctProbability = 1;
          for (let index = 0; index < drawsPerGroup; index += 1)
            distinctProbability *= (categoryCount - index) / categoryCount;
          collisionProbability = 1 - distinctProbability;
        }
        let collisions = 0;
        for (let index = 0; index < groupCount; index += 1) {
          if (context.random.next() < collisionProbability)
            collisions += 1;
        }
        return collisions;
      },
    })
    .register({
      descriptor: {
        name: 'sampleDistinctCount',
        title: '离散抽样去重计数',
        description: '从给定数量的等概率类别中有放回独立抽样，并返回样本中不同类别的数量。适用于占用、重复与碰撞机制。',
        category: 'random',
        minArgs: 2,
        maxArgs: 2,
        deterministic: false,
        batchScalarOutput: true,
        callForms: [
          {
            description: '执行一批等概率离散抽样并计算不同类别数。',
            arguments: [
              { name: 'count', description: '1 到 10000 的抽样次数。' },
              { name: 'categoryCount', description: '1 到 1000000 的可选类别数量。' },
            ],
          },
        ],
      },
      execute: ([rawCount, rawCategoryCount], context) => {
        const count = boundedInteger('sampleDistinctCount', rawCount, 10000);
        const categoryCount = boundedInteger(
          'sampleDistinctCount',
          rawCategoryCount,
          1_000_000,
        );
        const seen = new Set<number>();
        for (let index = 0; index < count; index += 1)
          seen.add(Math.floor(context.random.next() * categoryCount));
        return seen.size;
      },
    })
    .register({
      descriptor: {
        name: 'sampleCoverageDrawCount',
        title: '离散类别全覆盖抽取次数',
        description: '从等概率类别中有放回独立抽样，直到全部类别至少出现一次，并返回完成覆盖所需的抽取次数。一次调用代表一轮完整独立试验。',
        category: 'random',
        minArgs: 1,
        maxArgs: 1,
        deterministic: false,
        batchScalarOutput: true,
        callForms: [
          {
            description: '从空集合开始执行一轮完整的等概率类别覆盖试验。',
            arguments: [
              { name: 'categoryCount', description: '1 到 1000 的类别总数。' },
            ],
          },
        ],
      },
      execute: ([rawCategoryCount], context) => {
        const categoryCount = boundedInteger(
          'sampleCoverageDrawCount',
          rawCategoryCount,
          1000,
        );
        const seen = new Set<number>();
        let draws = 0;
        const maximumDraws = 1_000_000;
        while (seen.size < categoryCount) {
          draws += 1;
          if (draws > maximumDraws) {
            throw new Error(
              `sampleCoverageDrawCount 超过安全抽取上限 ${maximumDraws}`,
            );
          }
          seen.add(Math.floor(context.random.next() * categoryCount));
        }
        return draws;
      },
    })
    .register({
      descriptor: {
        name: 'sampleGeometricWaitCount',
        title: '几何分布首次成功等待次数',
        description: '按给定成功概率完成一轮独立伯努利序列，返回包含首次成功在内的试验次数。一次调用代表一轮完整独立几何分布试验。',
        category: 'random',
        minArgs: 1,
        maxArgs: 1,
        deterministic: false,
        batchScalarOutput: true,
        callForms: [
          {
            description: '从一次全新的独立试验开始，直接抽取一个几何等待次数标量。',
            arguments: [
              { name: 'probability', description: '单次试验成功概率，范围为 1e-9 到 1。' },
            ],
          },
        ],
      },
      execute: ([rawProbability], context) => {
        const probability = finite('sampleGeometricWaitCount', rawProbability);
        if (probability < 1e-9 || probability > 1) {
          throw new Error(
            'sampleGeometricWaitCount 的成功概率必须在 1e-9 到 1 之间',
          );
        }
        if (probability === 1)
          return 1;
        const uniform = context.random.next();
        const waitCount = Math.floor(
          Math.log1p(-uniform) / Math.log1p(-probability),
        ) + 1;
        if (!Number.isSafeInteger(waitCount) || waitCount < 1)
          throw new Error('sampleGeometricWaitCount 的抽样结果超出安全整数范围');
        return waitCount;
      },
    })
    .register({
      descriptor: {
        name: 'binomial',
        title: '二项分布成功次数',
        description: '执行给定次数的独立伯努利试验，并返回其中成功的次数。适合把一整批二元结果表示为当前快照中的一个标量。',
        category: 'random',
        minArgs: 2,
        maxArgs: 2,
        deterministic: false,
        batchScalarOutput: true,
        callForms: [
          {
            description: '完成一批相互独立、成功概率相同的伯努利试验。',
            arguments: [
              { name: 'trials', description: '0 到 10000 的整数试验次数。' },
              { name: 'probability', description: '每次试验成功的概率，范围为 0 到 1。' },
            ],
          },
        ],
      },
      execute: ([rawTrials, rawProbability], context) => {
        const trials = boundedNonNegativeInteger('binomial', rawTrials, 10000);
        const probability = finite('binomial', rawProbability);
        if (probability < 0 || probability > 1)
          throw new Error('binomial 的成功概率必须在 0 到 1 之间');
        let successes = 0;
        for (let index = 0; index < trials; index += 1) {
          if (context.random.next() < probability)
            successes += 1;
        }
        return successes;
      },
    })
    .register({
      descriptor: {
        name: 'bernoulli',
        title: '伯努利分布',
        description: '按 [0, 1] 概率返回布尔结果。',
        category: 'random',
        minArgs: 1,
        maxArgs: 1,
        deterministic: false,
      },
      execute: ([rawProbability], context) => {
        const probability = finite('bernoulli', rawProbability);
        if (probability < 0 || probability > 1)
          throw new Error('伯努利分布概率必须在 0 到 1 之间');
        return context.random.next() < probability;
      },
    })
    .register({
      descriptor: {
        name: 'normal',
        title: '正态分布',
        description: '按均值和非负标准差采样。',
        category: 'random',
        minArgs: 0,
        maxArgs: 2,
        deterministic: false,
      },
      execute: (args, context) => {
        const mean = args[0] === undefined ? 0 : finite('normal', args[0]);
        const deviation = args[1] === undefined
          ? 1
          : finite('normal', args[1]);
        if (deviation < 0)
          throw new Error('正态分布标准差不能小于 0');
        return mean + deviation * standardNormal(context.random);
      },
    })
    .register({
      descriptor: {
        name: 'poisson',
        title: '泊松分布',
        description: '按非负速率参数采样泊松分布。',
        category: 'random',
        minArgs: 1,
        maxArgs: 1,
        deterministic: false,
      },
      execute: ([rawRate], context) => {
        const rate = finite('poisson', rawRate);
        if (rate < 0)
          throw new Error('泊松分布参数不能小于 0');
        if (rate === 0)
          return 0;
        if (rate >= 30) {
          return Math.max(
            0,
            Math.round(rate + Math.sqrt(rate) * standardNormal(context.random)),
          );
        }
        return poissonSample(context.random, rate);
      },
    });
}
